import type { ThreadRequestType, ThreadTaskSuggestionMessage } from '@frontend/api-types';
import { isLast, pluralize } from '@frontend/duck-tape';
import { Fragment, useVisibilityController } from '@frontend/react';
import { Clickable, Icon, Text, XStack } from '@frontend/web-react';
import { applyClass, concatClasses } from '@frontend/web-utils';
import { useAnalytics } from '@frontend/web/hooks';
import type { ThreadReferenceForTask } from '@frontend/web/routes/app/thread/$threadId';
import { ThreadSuggestionCard } from './ThreadSuggestionCard';

type ThreadSuggestionMessageProps = {
  isStreaming: boolean;
  isThrottled: boolean;
  message: ThreadTaskSuggestionMessage;
  messageIndex: number;
  onClickSuggestionCard: (researchThreadReferenceData: ThreadReferenceForTask) => void;
  researchThreadId: string;
  threadRequestType?: MaybeNil<ThreadRequestType>;
};

export const ThreadSuggestionMessage = ({
  isStreaming,
  isThrottled,
  message,
  messageIndex,
  onClickSuggestionCard,
  researchThreadId,
  threadRequestType,
}: ThreadSuggestionMessageProps) => {
  const [isExpanded, { toggle }] = useVisibilityController(true);
  const { track } = useAnalytics();

  const composedOnClickSuggestionCard = (researchThreadReferenceData: ThreadReferenceForTask) => {
    onClickSuggestionCard(researchThreadReferenceData);
    if (
      !message.content?.[researchThreadReferenceData.taskSuggestionIdx]?.taskId &&
      !message.content?.[researchThreadReferenceData.taskSuggestionIdx]?.recurrenceId
    ) {
      track({
        data: {
          id: researchThreadReferenceData.id,
          isRecurrence: researchThreadReferenceData.suggestion.isRecurrence,
          researchThreadId,
        },
        event: 'unusedSuggestedTaskCardSelected',
        topic: 'research',
      });
    }
  };

  if (!message.content.length) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Clickable className="gap-md ml-xl" disabled={isStreaming}>
      {threadRequestType === 'create' ? (
        <ThreadSuggestionCard
          isAnyStreaming={isStreaming}
          isCurrentStreaming={isStreaming}
          isStandalone
          isThrottled={isThrottled}
          onClick={() =>
            composedOnClickSuggestionCard({
              id: researchThreadId,
              messageIdx: messageIndex,
              suggestion: message.content[0]!,
              taskSuggestionIdx: 0,
            })
          }
          suggestion={message.content[0]!}
        />
      ) : (
        <Fragment>
          <XStack>
            <XStack className="gap-x-sm cursor-pointer items-center" onClick={toggle}>
              <Text color="secondary" isSelectable={false} type="h6">
                Suggested {pluralize('Task', message.content.length)}
              </Text>
              <Icon name={isExpanded ? 'IconChevronDown' : 'IconChevronRight'} />
            </XStack>
          </XStack>
          <XStack className={concatClasses('gap-sm flex-wrap', applyClass(isExpanded, '', 'hidden'))}>
            {message.content.map((suggestion, index, array) => (
              <ThreadSuggestionCard
                isAnyStreaming={isStreaming}
                isCurrentStreaming={Boolean(isStreaming ? isLast(array, index) : null)}
                isThrottled={isThrottled}
                key={index}
                onClick={() =>
                  composedOnClickSuggestionCard({
                    id: researchThreadId,
                    messageIdx: messageIndex,
                    suggestion,
                    taskSuggestionIdx: index,
                  })
                }
                suggestion={suggestion}
              />
            ))}
          </XStack>
        </Fragment>
      )}
    </Clickable>
  );
};
